// Theme Store Module
// Centralized dark/light mode state management

import { applyPaletteToCSS, loadSavedPalettes, savePalette } from '@/components/insights/shared/colorPalettes';

const THEME_STORAGE_KEY = 'cynergy-theme';

const state = {
  isDarkMode: false,
  lightPalette: 'default',
  darkPalette: 'default',
};

const getters = {
  isDarkMode: s => s.isDarkMode,
  currentTheme: s => (s.isDarkMode ? 'dark' : 'light'),
  lightPalette: s => s.lightPalette,
  darkPalette: s => s.darkPalette,
  // The active palette for the current theme mode
  activePalette: s => (s.isDarkMode ? s.darkPalette : s.lightPalette),
};

const mutations = {
  SET_DARK_MODE(currentState, value) {
    // eslint-disable-next-line no-param-reassign
    currentState.isDarkMode = value;
    document.documentElement.setAttribute('data-theme', value ? 'dark' : 'light');
    localStorage.setItem(THEME_STORAGE_KEY, value ? 'dark' : 'light');
    // Apply the correct palette for the new theme mode
    const paletteId = value ? currentState.darkPalette : currentState.lightPalette;
    applyPaletteToCSS(value, paletteId);
  },
  TOGGLE_DARK_MODE(currentState) {
    // eslint-disable-next-line no-param-reassign
    currentState.isDarkMode = !currentState.isDarkMode;
    document.documentElement.setAttribute('data-theme', currentState.isDarkMode ? 'dark' : 'light');
    localStorage.setItem(THEME_STORAGE_KEY, currentState.isDarkMode ? 'dark' : 'light');
    const paletteId = currentState.isDarkMode ? currentState.darkPalette : currentState.lightPalette;
    applyPaletteToCSS(currentState.isDarkMode, paletteId);
  },
  RESET_THEME(currentState) {
    // eslint-disable-next-line no-param-reassign
    currentState.isDarkMode = false;
    document.documentElement.removeAttribute('data-theme');
  },
  SET_LIGHT_PALETTE(currentState, paletteId) {
    // eslint-disable-next-line no-param-reassign
    currentState.lightPalette = paletteId;
    savePalette('light', paletteId);
    // Only apply if currently in light mode
    if (!currentState.isDarkMode) {
      applyPaletteToCSS(false, paletteId);
    }
  },
  SET_DARK_PALETTE(currentState, paletteId) {
    // eslint-disable-next-line no-param-reassign
    currentState.darkPalette = paletteId;
    savePalette('dark', paletteId);
    // Only apply if currently in dark mode
    if (currentState.isDarkMode) {
      applyPaletteToCSS(true, paletteId);
    }
  },
};

// Apply a user-initiated theme change inside a View Transition so the new theme
// reveals left-to-right with a soft edge (see ::view-transition rules in app.scss).
// Where the API is unavailable the change is applied instantly. Not used on boot.
function applyThemeWithTransition(apply) {
  if (typeof document === 'undefined' || typeof document.startViewTransition !== 'function') {
    apply();
    return;
  }
  document.startViewTransition(apply);
}

const actions = {
  initTheme({ commit }) {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (savedTheme) {
      commit('SET_DARK_MODE', savedTheme === 'dark');
    } else {
      commit('SET_DARK_MODE', false);
    }
    const saved = loadSavedPalettes();
    commit('SET_LIGHT_PALETTE', saved.light);
    commit('SET_DARK_PALETTE', saved.dark);
  },
  setDarkMode({ commit }, value) {
    applyThemeWithTransition(() => commit('SET_DARK_MODE', value));
  },
  toggleDarkMode({ commit }) {
    applyThemeWithTransition(() => commit('TOGGLE_DARK_MODE'));
  },
  resetTheme({ commit }) {
    commit('RESET_THEME');
  },
  setLightPalette({ commit }, paletteId) {
    commit('SET_LIGHT_PALETTE', paletteId);
  },
  setDarkPalette({ commit }, paletteId) {
    commit('SET_DARK_PALETTE', paletteId);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
